<script setup>
import { useCurrency } from 'dashboard/composables/useCurrency';
import MetricHelp from '../../components/MetricHelp.vue';

defineProps({
  data: { type: Object, default: () => ({}) },
});

const { formatCurrency } = useCurrency();

const formatDate = value => value?.split('-').reverse().join('/') || '';

// Nome e "?" de cada indicador vêm do dicionário CRM_METRICS (mesmos de todas as telas).
const metrics = [
  ['leads', 'LEADS', 'i-lucide-layout-panel-top'],
  ['sales', 'SALES', 'i-lucide-trophy'],
  ['sales_value', 'SALES_VALUE', 'i-lucide-banknote', true],
  ['losses', 'LOSSES', 'i-lucide-circle-x'],
  ['appointments', 'APPOINTMENTS', 'i-lucide-calendar-check'],
  ['meetings_held', 'MEETINGS_HELD', 'i-lucide-calendar-check-2'],
  ['meetings_unmarked', 'MEETINGS_UNMARKED', 'i-lucide-calendar-clock'],
  ['outbound_calls', 'OUTBOUND_CALLS', 'i-lucide-phone-outgoing'],
  ['leads_not_called', 'LEADS_NOT_CALLED', 'i-lucide-phone-missed'],
];

const sdrColumns = [
  ['appointments', 'APPOINTMENTS'],
  ['meetings_held', 'MEETINGS_HELD'],
  ['meetings_no_show', 'MEETINGS_NO_SHOW'],
  ['meetings_unmarked', 'MEETINGS_UNMARKED'],
  ['sdr_sales', 'SDR_SALES'],
  ['sdr_sales_value', 'SDR_SALES_VALUE', true],
];
</script>

<template>
  <section class="space-y-6">
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ $t('DEAL_REPORTS.OPERATIONS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-10">
        {{ $t('DEAL_REPORTS.OPERATIONS.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      <div
        v-for="[key, metricKey, icon, isMoney] in metrics"
        :key="key"
        class="flex min-h-28 flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="flex items-start justify-between gap-2">
          <span class="text-lg text-n-blue-10" :class="[icon]" />
          <MetricHelp :metric-key="metricKey" />
        </div>
        <p class="mt-auto pt-3 text-2xl font-semibold text-n-slate-12">
          {{
            isMoney
              ? formatCurrency(data.summary?.[key] || 0)
              : data.summary?.[key] || 0
          }}
        </p>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t(`CRM_METRICS.${metricKey}.NAME`) }}
        </p>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
      <h3 class="mb-3 font-semibold text-n-slate-12">
        {{ $t('DEAL_REPORTS.OPERATIONS.BY_SDR') }}
      </h3>
      <div v-if="data.by_sdr?.length" class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="text-xs text-n-slate-9">
            <tr>
              <th class="py-2">
                {{ $t('CRM_METRICS.TEAM_PERFORMANCE.NAME') }}
              </th>
              <th
                v-for="[, metricKey] in sdrColumns"
                :key="metricKey"
                class="py-2 pl-3"
              >
                <span class="flex items-center gap-1">
                  {{ $t(`CRM_METRICS.${metricKey}.NAME`) }}
                  <MetricHelp :metric-key="metricKey" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in data.by_sdr" :key="row.key">
              <td class="py-2 font-medium text-n-slate-12">{{ row.name }}</td>
              <td
                v-for="[field, metricKey, isMoney] in sdrColumns"
                :key="metricKey"
                class="py-2 pl-3 text-n-slate-11"
              >
                {{
                  isMoney ? formatCurrency(row[field] || 0) : row[field] || 0
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-sm text-n-slate-10">
        {{ $t('DEAL_REPORTS.OPERATIONS.NO_DATA') }}
      </p>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="font-semibold text-n-slate-12">
          {{ $t('DEAL_REPORTS.OPERATIONS.NOT_CALLED_CLIENTS') }}
        </h3>
        <span
          class="rounded-full bg-n-amber-3 px-2.5 py-1 text-xs font-semibold text-n-amber-11"
        >
          {{ data.summary?.leads_not_called || 0 }}
        </span>
      </div>
      <div
        class="grid max-h-[32rem] grid-cols-1 gap-3 overflow-y-auto pr-2 md:grid-cols-2"
      >
        <div
          v-for="row in data.not_called"
          :key="row.card_id"
          class="flex min-w-0 items-center justify-between gap-4 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
        >
          <div class="min-w-0">
            <p class="truncate font-medium text-n-slate-12">
              {{ row.contact_name || row.phone }}
            </p>
            <p class="truncate text-xs text-n-slate-10">
              {{ row.phone }} ·
              {{ row.assignee || $t('DEAL_REPORTS.OPERATIONS.UNASSIGNED') }}
            </p>
            <p
              class="mt-1 inline-flex max-w-full rounded bg-n-slate-3 px-2 py-0.5 text-xs text-n-slate-10"
            >
              {{ row.stage_name }}
            </p>
          </div>
          <span class="shrink-0 text-xs text-n-slate-9"
            >#{{ row.conversation_id }}</span
          >
        </div>
        <p
          v-if="!data.not_called?.length"
          class="text-sm text-n-slate-10 md:col-span-2"
        >
          {{ $t('DEAL_REPORTS.OPERATIONS.NO_DATA') }}
        </p>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
      <h3 class="mb-4 font-semibold text-n-slate-12">
        {{ $t('DEAL_REPORTS.OPERATIONS.DAILY') }}
      </h3>
      <div class="max-h-96 overflow-y-auto pr-2">
        <table class="w-full text-left text-sm">
          <thead class="text-xs uppercase text-n-slate-9">
            <tr>
              <th class="py-2">{{ $t('DEAL_REPORTS.OPERATIONS.DATE') }}</th>
              <th class="py-2 text-right">
                {{ $t('DEAL_REPORTS.OPERATIONS.CARDS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in data.daily" :key="row.date">
              <td class="py-3 text-n-slate-11">
                {{ formatDate(row.date) }}
              </td>
              <td class="py-2 text-right font-semibold text-n-slate-12">
                {{ row.cards }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
